import { Fragment, type ReactNode } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import { MembersToolbarButton } from './MembersToolbarButton';

interface MembersManagementPaginationProps<PageSize extends number> {
  readonly page: number;
  readonly pageCount: number;
  readonly pageSize: PageSize;
  readonly pageSizeOptions: readonly PageSize[];
  readonly totalLabel: ReactNode;
  readonly onPageChange: (page: number) => void;
  readonly onPageSizeChange: (pageSize: PageSize) => void;
  readonly id?: string;
}

function getPaginationItems(page: number, pageCount: number) {
  if (pageCount <= 1) return [1];
  const pages = new Set<number>([1, page, pageCount]);
  if (page > 2) pages.add(page - 1);
  if (page < pageCount - 1) pages.add(page + 1);
  return Array.from(pages).sort((left, right) => left - right);
}

export function MembersManagementPagination<PageSize extends number>({
  page,
  pageCount,
  pageSize,
  pageSizeOptions,
  totalLabel,
  onPageChange,
  onPageSizeChange,
  id = 'members-pagination',
}: MembersManagementPaginationProps<PageSize>) {
  const paginationItems = getPaginationItems(page, pageCount);
  const href = `#${id}`;

  return (
    <div id={id} className="grid gap-3 border-t border-border px-6 py-4 md:grid-cols-[1fr_auto_1fr] md:items-center">
      <p className="text-sm text-muted-foreground">{totalLabel}</p>
      <Pagination className="justify-center">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              href={href}
              aria-disabled={page <= 1}
              tabIndex={page <= 1 ? -1 : 0}
              className={cn(page <= 1 && 'pointer-events-none opacity-50')}
              onClick={(event) => {
                event.preventDefault();
                if (page > 1) onPageChange(page - 1);
              }}
            />
          </PaginationItem>
          {paginationItems.map((pageNumber, index) => {
            const previousPage = paginationItems[index - 1];
            const hasGap = previousPage !== undefined && pageNumber - previousPage > 1;
            return (
              <Fragment key={pageNumber}>
                {hasGap ? <PaginationItem><PaginationEllipsis /></PaginationItem> : null}
                <PaginationItem>
                  <PaginationLink
                    href={href}
                    isActive={pageNumber === page}
                    aria-label={`Aller à la page ${pageNumber}`}
                    onClick={(event) => {
                      event.preventDefault();
                      onPageChange(pageNumber);
                    }}
                  >
                    {pageNumber}
                  </PaginationLink>
                </PaginationItem>
              </Fragment>
            );
          })}
          <PaginationItem>
            <PaginationNext
              href={href}
              aria-disabled={page >= pageCount}
              tabIndex={page >= pageCount ? -1 : 0}
              className={cn(page >= pageCount && 'pointer-events-none opacity-50')}
              onClick={(event) => {
                event.preventDefault();
                if (page < pageCount) onPageChange(page + 1);
              }}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
      <div className="flex justify-start md:justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <MembersToolbarButton className="h-10 justify-between px-4 text-sm">
              {pageSize} lignes
              <ChevronDownIcon data-icon="inline-end" />
            </MembersToolbarButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuGroup>
              <DropdownMenuLabel>Lignes par page</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuRadioGroup
                value={String(pageSize)}
                onValueChange={(value) => onPageSizeChange(Number(value) as PageSize)}
              >
                {pageSizeOptions.map((option) => (
                  <DropdownMenuRadioItem key={option} value={String(option)}>
                    {option} lignes
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
